import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBUserPermissionSchema, UserPermissionSchema } from './schemas';

type DBUserPermission = z.infer<typeof DBUserPermissionSchema>;
type UserPermission = z.infer<typeof UserPermissionSchema>;

/**
 * Helper function to convert permissions from User format (array) to DB format (record)
 */
const permissionsArrayToRecord = (permissions: UserPermission['permissions']) => {
  return Object.fromEntries(
    permissions.map(permission => [permission.value, { sources: permission.sources }])
  );
};

/**
 * Helper function to convert permissions from DB format (record) to User format (array)
 */
const permissionsRecordToArray = (permissions: DBUserPermission['permissions'] = {}) => {
  return Object.entries(permissions).map(([permissionId, { sources }]) => ({
    value: permissionId,
    sources,
  }));
};

export const UserPermissionCodecConfig: TransformerConfig<
  typeof UserPermissionSchema,
  typeof DBUserPermissionSchema
> = {
  name: 'UserPermission',
  clientSchema: UserPermissionSchema,
  dbSchema: DBUserPermissionSchema,
  transformers: [
    // permissions: [{ value, sources }] ↔ permissions: { value: { sources } }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['permissions'] as const,
        encode: ({ permissions }) => ({
          permissions: permissionsArrayToRecord(permissions),
        }),
      },
      decoder: {
        sourceFields: ['permissions'] as const,
        decode: ({ permissions }) => ({
          permissions: permissionsRecordToArray(permissions),
        }),
      },
    }),

    // examPermissions: [{ value, sources }] ↔ examPermissions: { value: { sources } }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['examPermissions'] as const,
        encode: ({ examPermissions }) => ({
          examPermissions: permissionsArrayToRecord(examPermissions),
        }),
      },
      decoder: {
        sourceFields: ['examPermissions'] as const,
        decode: ({ examPermissions }) => ({
          examPermissions: permissionsRecordToArray(examPermissions),
        }),
      },
    }),

    // notePermissions: [{ value, sources }] ↔ notePermissions: { value: { sources } }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['notePermissions'] as const,
        encode: ({ notePermissions }) => ({
          notePermissions: permissionsArrayToRecord(notePermissions),
        }),
      },
      decoder: {
        sourceFields: ['notePermissions'] as const,
        decode: ({ notePermissions }) => ({
          notePermissions: permissionsRecordToArray(notePermissions),
        }),
      },
    }),

    // videoPermissions: [{ value, sources }] ↔ videoPermissions: { value: { sources } }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['videoPermissions'] as const,
        encode: ({ videoPermissions }) => ({
          videoPermissions: permissionsArrayToRecord(videoPermissions),
        }),
      },
      decoder: {
        sourceFields: ['videoPermissions'] as const,
        decode: ({ videoPermissions }) => ({
          videoPermissions: permissionsRecordToArray(videoPermissions),
        }),
      },
    }),

    // tutorialPermissions: [{ value, sources }] ↔ tutorialPermissions: { value: { sources } }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['tutorialPermissions'] as const,
        encode: ({ tutorialPermissions }) => ({
          tutorialPermissions: permissionsArrayToRecord(tutorialPermissions),
        }),
      },
      decoder: {
        sourceFields: ['tutorialPermissions'] as const,
        decode: ({ tutorialPermissions }) => ({
          tutorialPermissions: permissionsRecordToArray(tutorialPermissions),
        }),
      },
    }),

    // permissionGroups: [groupId] ↔ permissionGroups: { groupId: {} }
    createTransformer<typeof UserPermissionSchema, typeof DBUserPermissionSchema>({
      encoder: {
        sourceFields: ['permissionGroups'] as const,
        encode: ({ permissionGroups }) => ({
          permissionGroups: Object.fromEntries(permissionGroups.map(groupId => [groupId, {}])),
        }),
      },
      decoder: {
        sourceFields: ['permissionGroups'] as const,
        decode: ({ permissionGroups = {} }) => ({
          permissionGroups: Object.keys(permissionGroups),
        }),
      },
    }),
  ],
};

export const encodeUserPermissionToDb = createEncodeToDbFn(UserPermissionCodecConfig);
export const decodeUserPermissionFromDb = createDecodeFromDbFn(UserPermissionCodecConfig);
